/**
 * The two things a tick in a package means, said once for every list that
 * has ticks (capabilities, module headers, map content).
 *
 * A package decides access only for a row that is PACKAGE-ONLY (the lock):
 * ticked there, customers whose license or add-on carries the package get it,
 * nobody else does. Every other row is FREE FOR EVERYONE today — every
 * customer has it whatever a package says — and its tick is a PLACEMENT:
 * it records that this package keeps the row if it is ever made
 * package-only on Feature access (the package picker there pre-selects the
 * packages that already carry it). So a license reads as the complete product
 * before anything is locked, and the day a capability is locked the right
 * packages already keep it.
 *
 * The checkbox itself says which: a full tick is access this package gives,
 * a dash is a placement (carried, free today), an empty box is not carried.
 * Nothing about this is a fault to fix — a free row no package carries is
 * simply unplaced, which Feature access lists in its "Packages" column — so
 * the editor explains on hover and marks nothing in amber.
 */

export type TickMeaning =
  /** Package-only: the tick is the access. */
  | "access"
  /** Free today and carried by this package: the tick is a placement. */
  | "placement"
  /** Free today and carried by no package at all. */
  | "unplaced"
  /** Free today, not in this package, carried by another. */
  | "placedElsewhere"

export type TickMeaningInput = {
  isPackageOnly: boolean
  /** Whether the package being edited carries the row (by pick or by rule). */
  isCarriedHere: boolean
  /** Every package carrying the row, this one included. */
  packageNames: readonly string[]
}

export function tickMeaningOf(input: TickMeaningInput): TickMeaning {
  const { isPackageOnly, isCarriedHere, packageNames } = input
  if (isPackageOnly) return "access"
  if (isCarriedHere) return "placement"
  return packageNames.length === 0 ? "unplaced" : "placedElsewhere"
}

/**
 * What the row's checkbox shows: a placement is a dash, access a tick. The
 * checkbox is the one glyph every reader looks at, so it carries the
 * meaning and the tooltip spells it out.
 */
export function tickStateOf(
  isCarriedHere: boolean,
  meaning: TickMeaning,
): boolean | "indeterminate" {
  if (!isCarriedHere) return false
  return meaning === "placement" ? "indeterminate" : true
}

export type TickTooltipInput = {
  meaning: TickMeaning
  isCarriedHere: boolean
  packageName: string
  rowName: string
  /** The other packages carrying the row, for a row this package does not carry. */
  otherPackages: readonly string[]
}

/** The tooltip on a row's checkbox: what this tick does, in one or two sentences. */
export function describeTick(input: TickTooltipInput) {
  const { meaning, isCarriedHere, packageName, rowName, otherPackages } = input
  if (meaning === "access") {
    return isCarriedHere
      ? `Unlocked by ${packageName}: ${rowName} is package-only, and customers whose license or add-on carries ${packageName} get it.`
      : `Not unlocked by ${packageName}: ${rowName} is package-only, so a customer needs a package that carries it${otherPackages.length > 0 ? ` (today: ${otherPackages.join(", ")})` : " — and no package does yet"}.`
  }
  if (meaning === "placement") {
    return `Placed, not unlocked: ${rowName} is free for every customer today, so this changes no one's access. It records that ${packageName} keeps ${rowName} if it is ever made package-only on Feature access, where ${packageName} will come pre-selected.`
  }
  if (meaning === "placedElsewhere") {
    return `${rowName} is free for every customer today and placed in ${otherPackages.join(", ")}, not in ${packageName}. Tick it to place it here too.`
  }
  return `${rowName} is free for every customer today and placed in no package. Nothing changes for anyone; the day it is made package-only, a package has to carry it for a customer to keep it.`
}

/** The "Who gets it" fact on a hover card. */
export function describeWhoGetsIt(isPackageOnly: boolean) {
  return isPackageOnly
    ? "only customers whose license or add-on carries a package with it (package-only)"
    : "every customer today — free for everyone, whatever a package says"
}

/**
 * The packages fact on a hover card: for a package-only row the packages
 * ARE the access; for a free row they are where it goes the day it is
 * locked.
 */
export function describePackagesFact(
  isPackageOnly: boolean,
  packageNames: readonly string[],
): { label: string; value: string } {
  const names = packageNames.join(", ")
  if (isPackageOnly) {
    return {
      label: "In packages",
      value:
        names ||
        "none — package-only, and no package carries it: nobody has it",
    }
  }
  return {
    label: "Placed in",
    value: names
      ? `${names} — keeps it if it is ever made package-only; changes nothing today`
      : "no package yet — free for everyone today; a package has to carry it before it can be made package-only",
  }
}

/**
 * "17 of 98 in Basic · 17 placed (free today)": the capabilities section's
 * one-line summary through the package lens, split by what the ticks mean.
 */
export function describePackageCapabilities(input: {
  packageName: string
  total: number
  carried: number
  carriedPackageOnly: number
}) {
  const { packageName, total, carried, carriedPackageOnly } = input
  const parts = [`${carried} of ${total} in ${packageName}`]
  if (carriedPackageOnly > 0) {
    parts.push(`${carriedPackageOnly} unlocked here (package-only)`)
  }
  const carriedFree = carried - carriedPackageOnly
  if (carriedFree > 0) parts.push(`${carriedFree} placed here (free today)`)
  return parts.join(" · ")
}
